import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import COLORS, {useBuildingKey, getAllUniqueRiskTypesFromLevel, getAllUniqueHazardsFromLevel} from "../../Helpers/utils";
import {translate} from "../../types/EnumTranslations";
import "./LevelPage.css";
import BuildingCustomButton from "../BuildingDetails/BuildingCustomButton";
import {Helmet} from "react-helmet-async";
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import InfoPanel from "../../Components/InfoPanel";
import {BaseComponentImpl, RoomImpl, LevelImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {PhotoModel} from "../../types/protocols";
import {BaseType} from "../../types/Enums";
import BlueprintWithMarkers from "../../Components/BlueprintWithMarkers";
import {getComponentsWithBlueprintLocationsForRoom} from "../../Helpers/ComponentRoomUtils";
import NestedRiskConstruction from "../../Components/NestedRiskConstruction";
import NestedHazardView from "../../Components/NestedHazardView";
import {useAuthState} from "react-firebase-hooks/auth";
import {auth} from "../../firebase";
import {useUsersData} from "../../Helpers/UsersContext";
import BlueprintUploadModal from "../../Components/BlueprintUploadModal";

const LevelPage: React.FC = () => {
    const navigate = useNavigate();
    const buildingKey = useBuildingKey();
    const [hoveredRoomKey, setHoveredRoomKey] = useState<string | null>(null);
    const [hoveredUtilityKey, setHoveredUtilityKey] = useState<string | null>(null);
    const [level, setLevel] = useState<LevelImpl|undefined>(undefined);
    const [currentBlueprint, setCurrentBlueprint] = useState<PhotoModel | undefined>(undefined);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [showBlueprintUpload, setShowBlueprintUpload] = useState<boolean>(false);
    const [showComponents, setShowComponents] = useState<boolean>(false);
    const [showRooms, setShowRooms] = useState<boolean>(true);
    const location = useLocation();
    const pathSegments = location.pathname.split("/");
    const levelKey = pathSegments[pathSegments.length - 1];

    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();
    const [user] = useAuthState(auth);
    const { users } = useUsersData();

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    useEffect(() => {
        if (currentBuilding && levelKey) {
            const foundLevel = currentBuilding.model.structure?.levels?.[levelKey];
            setLevel(foundLevel ? new LevelImpl(foundLevel) : undefined);

            if (foundLevel) {
                // Get the current blueprint based on currentBlueprintKey
                if (foundLevel.currentBlueprintKey && foundLevel.blueprints?.[foundLevel.currentBlueprintKey]) {
                    setCurrentBlueprint(foundLevel.blueprints[foundLevel.currentBlueprintKey]);
                } else if (foundLevel.planImage) {
                    // Fallback to planImage if no current blueprint is set
                    setCurrentBlueprint(foundLevel.planImage);
                } else {
                    setCurrentBlueprint(undefined);
                }
            }
        }
    }, [currentBuilding, levelKey]);

    // Check if current user is admin
    useEffect(() => {
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            setIsAdmin(currentUser?.admin || false);
        }
    }, [user, users]);

    if (loadingBuilding) return <></>;
    if (buildingError) return <p>{buildingError}</p>;
    if (!currentBuilding) return <p>Building data missing</p>;
    if (!level) return <>Level data missing</>;

    const rooms: RoomImpl[] = currentBuilding.model.structure?.rooms
        ? Object.values(currentBuilding.model.structure.rooms).filter(
            (room) => room.levelKey === level.key
        ).map((item) => new RoomImpl(item))
        : [];

    // Get all components that belong to this level (through rooms)
    // This is calculated independently of showRooms to avoid state synchronization issues
    const allLevelComponents: BaseComponentImpl[] = level.currentBlueprintKey
        ? rooms.flatMap(room => 
            getComponentsWithBlueprintLocationsForRoom(room, currentBuilding, level.currentBlueprintKey!)
          )
        : [];
    
    // Only include components when showComponents is true
    const levelComponents: BaseComponentImpl[] = showComponents ? allLevelComponents : [];

    const handleMarkerClick = (roomKey: string) => {
        navigate(`/building/${buildingKey}/room/${roomKey}`);
    };

    const handleMouseEnter = (roomKey: string) => {
        setHoveredRoomKey(roomKey);
    };

    const handleMouseLeave = () => {
        setHoveredRoomKey(null);
    };

    const handleUtilityClick = (utilityKey: string) => {
        // Navigate to component page in same tab
        const componentUrl = `/building/${buildingKey}/component/${utilityKey}`;
        navigate(componentUrl);
    };

    const handleUtilityMouseEnter = (utilityKey: string) => {
        setHoveredUtilityKey(utilityKey);
    };

    const handleUtilityMouseLeave = () => {
        setHoveredUtilityKey(null);
    };

    const handleBlueprintUploadSuccess = async (newBlueprintKey: string) => {
        // Refresh the building data to get the updated blueprint
        if (buildingKey) {
            await fetchBuilding(buildingKey);
        }
    };

    const roomKeys = Object.values(rooms).map((room => room.key))

    const utilities = currentBuilding.model.structure?.components ?
        Object.values(currentBuilding.model.structure?.components)
            .filter((component) =>
                component.type === BaseType.Utility &&
                component.parentRoomKeys?.value?.some(key => roomKeys.includes(key))
            )
            .map((component) => {
                return new BaseComponentImpl(component)
            }) : [];

    const availableBlueprints = level.blueprints ? Object.keys(level.blueprints).length : 0;

    // Get risk constructions and hazards for this level
    const riskConstructions = getAllUniqueRiskTypesFromLevel(currentBuilding, levelKey);
    const hazards = getAllUniqueHazardsFromLevel(currentBuilding, levelKey);

    // Calculate total areas for the entire level (all rooms combined)
    const allRoomKeys = rooms.map(room => room.key);

    // Calculate total ceiling area for the level
    const levelCeilingComponents = Object.values(currentBuilding.model.structure?.components || {})
        .filter((component) => 
            component.type === BaseType.Ceiling && 
            component.roomKey && 
            allRoomKeys.includes(component.roomKey)
        )
        .map(component => new BaseComponentImpl(component));
    
    const totalLevelCeilingArea = levelCeilingComponents.reduce((total, ceiling) => {
        const area = ceiling.totalArea(currentBuilding);
        return total + (area || 0);
    }, 0);

    // Calculate total wall area for the level
    const levelWallComponents = Object.values(currentBuilding.model.structure?.components || {})
        .filter((component) => 
            component.type === BaseType.Wall && 
            component.roomKey && 
            allRoomKeys.includes(component.roomKey)
        )
        .map(component => new BaseComponentImpl(component));
    
    const totalLevelWallArea = levelWallComponents.reduce((total, wall) => {
        const area = wall.totalArea(currentBuilding);
        return total + (area || 0);
    }, 0);

    // Calculate total floor area for the level
    const levelFloorComponents = Object.values(currentBuilding.model.structure?.components || {})
        .filter((component) => 
            component.type === BaseType.Floor && 
            component.roomKey && 
            allRoomKeys.includes(component.roomKey)
        )
        .map(component => new BaseComponentImpl(component));
    
    const totalLevelFloorArea = levelFloorComponents.reduce((total, floor) => {
        const area = floor.totalArea(currentBuilding);
        return total + (area || 0);
    }, 0);

    return (
        <>
            <BreadcrumbComponent
                items={[
                    {text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${buildingKey}`},
                    {text: level.displayName()}
                ]}
            />

            <div style={{ padding: "20px", maxWidth: '1500px'}}>
                <Helmet>
                    <title>{`${level.displayName()}: ${currentBuilding.summary.name}`}</title>
                </Helmet>

                <div style={{marginBottom: '20px'}}>
                    <h3 style={{margin: 0}}>
                        {level.displayName()}
                    </h3>
                </div>
                <div
                    style={{
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '40px',
                        marginBottom: '20px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '60px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                >

                    <div style={{ flex: "2", height: 'fit-content', minWidth: '300px' }}>
                        {currentBlueprint && level.currentBlueprintKey ? (
                            <div>
                                {/* Title with checkboxes */}
                                <div className="blueprint-title-container">
                                    <h4 style={{ margin: 0 }}>Ritning</h4>
                                    <div className="blueprint-checkboxes">
                                        <div className="component-toggle">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={showRooms}
                                                    onChange={(e) => setShowRooms(e.target.checked)}
                                                />
                                                <span>Visa rum</span>
                                            </label>
                                        </div>
                                        <div className="component-toggle">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={showComponents}
                                                    onChange={(e) => setShowComponents(e.target.checked)}
                                                />
                                                <span>Visa komponenter</span>
                                            </label>
                                        </div>
                                    </div>
                                    {isAdmin && (
                                        <button
                                            onClick={() => setShowBlueprintUpload(true)}
                                            style={{
                                                backgroundColor: COLORS.primaryDark,
                                                color: 'white',
                                                border: 'none',
                                                padding: '6px 12px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                fontWeight: '400'
                                            }}
                                        >
                                            Byt ritning
                                        </button>
                                    )}
                                </div>
                                
                                <BlueprintWithMarkers
                                    key={`blueprint-${showRooms}-${showComponents}-${levelComponents.length}`}
                                    blueprint={currentBlueprint}
                                    currentBlueprintKey={level.currentBlueprintKey}
                                    building={currentBuilding}
                                    rooms={showRooms ? rooms : []}
                                    utilities={showComponents ? levelComponents : []}
                                    hoveredRoomKey={hoveredRoomKey}
                                    hoveredUtilityKey={hoveredUtilityKey}
                                    onRoomClick={handleMarkerClick}
                                    onUtilityClick={handleUtilityClick}
                                    onRoomMouseEnter={handleMouseEnter}
                                    onRoomMouseLeave={handleMouseLeave}
                                    onUtilityMouseEnter={handleUtilityMouseEnter}
                                    onUtilityMouseLeave={handleUtilityMouseLeave}
                                    title=""
                                />
                            </div>
                        ) : (
                            <div>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                                    <h4 style={{margin: 0}}>Ritning</h4>
                                    {isAdmin && (
                                        <button
                                            onClick={() => setShowBlueprintUpload(true)}
                                            style={{
                                                backgroundColor: COLORS.primaryDark,
                                                color: 'white',
                                                border: 'none',
                                                padding: '6px 12px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontSize: '12px',
                                                fontWeight: '400'
                                            }}
                                        >
                                            Byt ritning
                                        </button>
                                    )}
                                </div>
                                <div>Ritning saknas</div>
                            </div>
                        )}
                    </div>

                    <div style={{ flex: "1" }}>
                        <h4 style={{marginBottom: '20px'}}>Rum</h4>
                        {rooms.length > 0 ? (
                            <div>
                                {rooms
                                    .sort((roomA, roomB) => {
                                        if (roomA.number && roomB.number) {
                                            const numA = parseFloat(roomA.number);
                                            const numB = parseFloat(roomB.number);

                                            if (!isNaN(numA) && !isNaN(numB)) {
                                                return numA - numB;
                                            }
                                        }
                                        if (roomA.number && !roomB.number) return -1;
                                        if (!roomA.number && roomB.number) return 1;
                                        return roomA.key.localeCompare(roomB.key);
                                    })
                                    .map((room) => {
                                        // Check if room has location on current blueprint
                                        const hasCurrentBlueprintLocation = level.currentBlueprintKey &&
                                            room.blueprintLocations &&
                                            room.blueprintLocations[level.currentBlueprintKey];

                                        // Calculate floor area for this specific room
                                        const roomFloorComponents = Object.values(currentBuilding.model.structure?.components || {})
                                            .filter((component) => 
                                                component.type === BaseType.Floor && 
                                                component.roomKey === room.key
                                            )
                                            .map(component => new BaseComponentImpl(component));
                                        
                                        const roomFloorArea = roomFloorComponents.reduce((total, floor) => {
                                            const area = floor.totalArea(currentBuilding);
                                            return total + (area || 0);
                                        }, 0);

                                        const roomFloorAreaText = roomFloorArea > 0 ? `${roomFloorArea.toFixed(1)} kvm` : 'Okänd kvm';

                                        return (
                                            <React.Fragment key={room.key}>
                                                <BuildingCustomButton
                                                    title={room.displayName()}
                                                    metadata={[
                                                        translate(room.type),
                                                        roomFloorAreaText,
                                                        ...(hasCurrentBlueprintLocation ? [] : ['Position ej markerad på ritning'])
                                                    ]}
                                                    imagePath={`rooms/${room.type ?? "room_general"}.png`}
                                                    onClick={() => handleMarkerClick(room.key)}
                                                    onMouseEnter={() => handleMouseEnter(room.key)}
                                                    onMouseLeave={handleMouseLeave}
                                                    isHovered={hoveredRoomKey === room.key}
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                            </div>
                        ) : (
                            <>Detta våningsplan innehåller inga rum</>
                        )}
                    </div>

                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h4 style={{marginBottom: '20px'}}>Fakta</h4>
                            <InfoPanel items={[
                                {label: 'Våning', value: level.roofPlan ? 'Takplan' : level.formattedNumber()},
                                {label: 'Eget namn', value: level.name || '-'},
                                {label: 'Antal rum', value: rooms.length},
                                {label: 'Takyta', value: totalLevelCeilingArea > 0 ? `${totalLevelCeilingArea.toFixed(1)} kvm` : "Okänd kvm"},
                                {label: 'Väggyta', value: totalLevelWallArea > 0 ? `${totalLevelWallArea.toFixed(1)} kvm` : "Okänd kvm"},
                                {label: 'Golvyta', value: totalLevelFloorArea > 0 ? `${totalLevelFloorArea.toFixed(1)} kvm` : "Okänd kvm"},
                                {label: 'Fastighetsteknik', value: `${utilities.length} st`},
                            ]}/>
                        </div>

                        {/* Risk Constructions and Hazards Section */}
                        {(riskConstructions.length > 0 || hazards.length > 0) && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {/* Risk Constructions Section */}
                                {riskConstructions.length !== 0 && (
                                    <NestedRiskConstruction
                                        riskConstructions={riskConstructions}
                                        buildingKey={buildingKey || ''}
                                    />
                                )}

                                {/* Hazards Section */}
                                {hazards.length !== 0 && (
                                    <NestedHazardView
                                        hazards={hazards}
                                        buildingKey={buildingKey || ''}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <BlueprintUploadModal
                isOpen={showBlueprintUpload}
                onClose={() => setShowBlueprintUpload(false)}
                onSuccess={handleBlueprintUploadSuccess}
                buildingKey={buildingKey || ''}
                levelKey={levelKey}
                authorUid={user?.uid || ''}
            />
        </>
    );
};

export default LevelPage;